<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AceleresMessageApi from 'dashboard/api/aceleres/messageActions';
import { useMapGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['close', 'openMessage']);

const { locale, t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const messages = ref([]);
const searchQuery = ref('');
const isLoading = ref(false);
const hasError = ref(false);

const normalizeText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const contactFor = message => message.conversation?.contact || {};
const contactName = message =>
  contactFor(message).name || t('CHAT_LIST.STARRED.UNKNOWN_CONTACT');
const contactInitial = message => contactName(message).trim().charAt(0).toUpperCase();
const isOutgoing = message => Number(message.message_type) === 1;
const directionLabel = message =>
  isOutgoing(message)
    ? `${t('CHAT_LIST.STARRED.YOU')} ▸ ${contactName(message)}`
    : `${contactName(message)} ▸ ${t('CHAT_LIST.STARRED.YOU')}`;

const firstAttachment = message => message.attachments?.[0] || null;
const attachmentLabel = message => {
  const type = firstAttachment(message)?.file_type;
  const keyByType = {
    image: 'PHOTO',
    audio: 'AUDIO',
    video: 'VIDEO',
    file: 'FILE',
    location: 'LOCATION',
    contact: 'CONTACT',
  };
  return t(`CHAT_LIST.STARRED.${keyByType[type] || 'ATTACHMENT'}`);
};
const messagePreview = message =>
  message.content?.trim() || attachmentLabel(message);

const filteredMessages = computed(() => {
  const query = normalizeText(searchQuery.value.trim());
  if (!query) return messages.value;

  return messages.value.filter(message =>
    normalizeText(
      `${contactName(message)} ${message.sender?.name || ''} ${messagePreview(message)}`
    ).includes(query)
  );
});

const dateValue = timestamp => new Date(Number(timestamp) * 1000);
const messageTime = message =>
  new Intl.DateTimeFormat(locale.value, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateValue(message.created_at));
const messageDate = message => {
  const date = dateValue(message.created_at);
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  if (isToday) return messageTime(message);
  return new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const loadMessages = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await AceleresMessageApi.starred(accountId.value);
    messages.value = data.payload || [];
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadMessages);
</script>

<template>
  <section class="flex h-full min-h-0 flex-1 flex-col bg-n-surface-1">
    <header
      class="flex h-16 shrink-0 items-center gap-3 border-b border-n-weak px-4"
    >
      <button
        type="button"
        class="flex size-9 items-center justify-center rounded-full text-n-slate-12 hover:bg-n-alpha-2"
        :aria-label="$t('CHAT_LIST.STARRED.BACK')"
        @click="emit('close')"
      >
        <span class="i-lucide-arrow-left size-5" />
      </button>
      <h2 class="m-0 min-w-0 flex-1 truncate text-lg font-semibold text-n-slate-12">
        {{ $t('CHAT_LIST.STARRED.TITLE') }}
      </h2>
    </header>

    <div class="shrink-0 border-b border-n-weak px-4 py-3">
      <label
        class="flex h-10 items-center gap-3 rounded-full bg-n-alpha-2 px-4 text-n-slate-10 focus-within:ring-1 focus-within:ring-n-brand"
      >
        <span class="i-lucide-search size-4 shrink-0" />
        <input
          v-model="searchQuery"
          type="text"
          class="m-0 h-full min-w-0 flex-1 !border-0 !bg-transparent p-0 text-sm text-n-slate-12 !shadow-none !outline-none !ring-0 focus:!border-0 focus:!bg-transparent focus:!shadow-none focus:!ring-0"
          :placeholder="$t('CHAT_LIST.STARRED.SEARCH')"
        />
      </label>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto bg-n-surface-2">
      <div v-if="isLoading" class="flex h-40 items-center justify-center">
        <Spinner class="text-n-brand" />
      </div>

      <div
        v-else-if="hasError"
        class="flex h-48 flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <span class="i-lucide-circle-alert size-6 text-n-ruby-9" />
        <p class="m-0 text-sm text-n-slate-11">
          {{ $t('CHAT_LIST.STARRED.LOAD_ERROR') }}
        </p>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white"
          @click="loadMessages"
        >
          {{ $t('CHAT_LIST.STARRED.RETRY') }}
        </button>
      </div>

      <div
        v-else-if="!filteredMessages.length"
        class="flex h-48 flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <span class="i-lucide-star size-7 text-n-slate-9" />
        <p class="m-0 text-sm text-n-slate-11">
          {{
            searchQuery
              ? $t('CHAT_LIST.STARRED.NO_RESULTS')
              : $t('CHAT_LIST.STARRED.EMPTY')
          }}
        </p>
      </div>

      <button
        v-for="message in filteredMessages"
        :key="message.id"
        type="button"
        class="block w-full border-b border-n-weak bg-n-surface-1 px-4 py-4 text-left hover:bg-n-alpha-1"
        @click="emit('openMessage', message)"
      >
        <span class="mb-3 flex min-w-0 items-center gap-2">
          <img
            v-if="contactFor(message).thumbnail"
            :src="contactFor(message).thumbnail"
            :alt="contactName(message)"
            class="size-8 shrink-0 rounded-full object-cover"
          />
          <span
            v-else
            class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-slate-4 text-xs font-semibold text-n-slate-11"
          >
            {{ contactInitial(message) }}
          </span>
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12">
            {{ directionLabel(message) }}
          </span>
          <span class="shrink-0 text-xs text-n-slate-10">
            {{ messageDate(message) }}
          </span>
          <span class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-9" />
        </span>

        <span
          class="block w-fit max-w-[88%] rounded-xl px-3 py-2 shadow-sm"
          :class="
            isOutgoing(message)
              ? 'ml-auto bg-n-teal-3 text-n-slate-12'
              : 'mr-auto border border-n-weak bg-n-surface-1 text-n-slate-12'
          "
        >
          <img
            v-if="
              firstAttachment(message)?.file_type === 'image' &&
              (firstAttachment(message)?.thumb_url ||
                firstAttachment(message)?.data_url)
            "
            :src="
              firstAttachment(message).thumb_url ||
              firstAttachment(message).data_url
            "
            :alt="$t('CHAT_LIST.STARRED.PHOTO')"
            class="mb-2 max-h-32 w-full rounded-lg object-cover"
          />
          <span class="line-clamp-4 whitespace-pre-wrap break-words text-sm">
            {{ messagePreview(message) }}
          </span>
          <span class="mt-1 flex items-center justify-end gap-1 text-xs text-n-slate-10">
            <span class="i-lucide-star size-3 fill-current" />
            {{ messageTime(message) }}
          </span>
        </span>
      </button>
    </div>
  </section>
</template>
